import { Injectable } from '@angular/core';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseApiService } from './base-api.service';
import type { ExportErrorsResponseApiDto, AutomationErrorsResponseApiDto } from 'src/app/shared/dto/export-errors-response.interface.dto';
import type { GrowthSignalsReportDetailsApiDto } from 'src/app/shared/dto/growth-signals-report-details.dto';
import { CompanySignalsResponseDto } from '../../dto/company-signals-response.dto';

@Injectable({
  providedIn: 'root',
})
export class SignalsApiService {
  private readonly url = 'service/contactcloud/growth-signals-settings';
  private readonly errorsUrl = 'service/contactcloud/signals/export/errors';
  private readonly manualRunUrl = 'service/contactcloud/growth-signals/manual-run';
  private readonly reportDetailsUrl = 'service/contactcloud/growth-signals/report';
  private readonly contactcloudURL: string = 'service/contactcloud';

  constructor(private readonly baseApiService: BaseApiService) {}

  getSignals(headers: { clientId: string; clientAccountId: string }): Observable<any[]> {
    const additionalHeaders = new HttpHeaders({
      'X-CB-Client-Id': headers.clientId,
      'X-CB-Client-Account-Id': headers.clientAccountId,
    });
    return this.baseApiService.get<any[]>(this.url, { showError: true, additionalHeaders });
  }

  createSignal(payload: Record<string, unknown>, headers: { clientId: string; clientAccountId: string }): Observable<any> {
    const additionalHeaders = new HttpHeaders({
      'X-CB-Client-Id': headers.clientId,
      'X-CB-Client-Account-Id': headers.clientAccountId,
    });
    return this.baseApiService.post<any>(this.url, payload, { showError: true, additionalHeaders });
  }

  updateSignal(signalId: number | string, payload: Record<string, unknown>, headers: { clientId: string; clientAccountId: string }): Observable<any> {
    const additionalHeaders = new HttpHeaders({
      'X-CB-Client-Id': headers.clientId,
      'X-CB-Client-Account-Id': headers.clientAccountId,
    });
    return this.baseApiService.patch<any>(`${this.url}/${signalId}`, payload, { showError: true, additionalHeaders });
  }

  deleteSignal(signalId: number | string, headers: { clientId: string; clientAccountId: string }): Observable<any> {
    const additionalHeaders = new HttpHeaders({
      'X-CB-Client-Id': headers.clientId,
      'X-CB-Client-Account-Id': headers.clientAccountId,
    });
    return this.baseApiService.delete<any>(`${this.url}/${signalId}`, { showError: true, additionalHeaders });
  }

  getExportErrors(headers: { clientId: string; clientAccountId: string }): Observable<ExportErrorsResponseApiDto | AutomationErrorsResponseApiDto[]> {
    const additionalHeaders = new HttpHeaders({
      'X-CB-Client-Id': headers.clientId,
      'X-CB-Client-Account-Id': headers.clientAccountId,
    });
    return this.baseApiService.get<ExportErrorsResponseApiDto | AutomationErrorsResponseApiDto[]>(
      this.errorsUrl,
      { showError: true, additionalHeaders },
    );
  }

  manualRunSignals(
    payload: { signal_ids: Array<number | string> },
    headers: { clientId: string; clientAccountId: string }
  ): Observable<unknown> {
    const additionalHeaders = new HttpHeaders({
      'X-CB-Client-Id': headers.clientId,
      'X-CB-Client-Account-Id': headers.clientAccountId,
    });
    return this.baseApiService.post<unknown>(this.manualRunUrl, payload, { showError: true, additionalHeaders });
  }

  getGrowthSignalsReportDetails(reportId: string): Observable<GrowthSignalsReportDetailsApiDto> {
    return this.baseApiService.get<GrowthSignalsReportDetailsApiDto>(`${this.reportDetailsUrl}/${reportId}/details`);
  }

  getCompanySignalSearch(payload: { companyId: string; page: number; pageSize: number }): Observable<CompanySignalsResponseDto> {
    const httpParams = new HttpParams()
      .set('company_id', payload.companyId)
      .set('page', payload.page)
      .set('page_size', payload.pageSize);
    return this.baseApiService.post(`${this.contactcloudURL}/signals/company`, null, { params: httpParams, showError: true });
  }
}

